<script setup lang="ts">
import { ref, onMounted } from 'vue';
import live1Png from '@/assets/img/home/page4/page4-live1.png';
import live2Png from '@/assets/img/home/page4/page4-live2.png';
import live3Png from '@/assets/img/home/page4/page4-live3.png';
import live4Png from '@/assets/img/home/page4/page4-live4.png';
import { useI18n } from 'vue-i18n'
const { t } = useI18n()  
// 定义图片列表数据
const liveImages = [
  { id: 1, img: live1Png, alt: '数字人直播电商' },
  { id: 2, img: live2Png, alt: '数字人旅游直播' },
  { id: 3, img: live3Png, alt: '数字人财税直播' },
  { id: 4, img: live4Png, alt: '数字人美食直播' }
];
const isVisible = ref(false);
const pageRef = ref<HTMLElement | null>(null);

// 监听页面可见性
onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          isVisible.value = true;
        } else {
          isVisible.value = false;
        }
      });
    },
    {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    }
  );

  if (pageRef.value) {
    observer.observe(pageRef.value);
  }
});
</script>

<template>
  <div ref="pageRef" class="flex flex-col items-center h-[49rem] bg-[#FAFAFA] ">
    <!-- 标题部分 -->
    <div class="text-center max-width mb-[5rem] mt-[8rem]">
      <h2 class="text-[3rem]  font-[600] text-[#000000] transition-all"
          :class="isVisible ? 'slide-down-in' : 'slide-down-out'">
        {{ t('page4.titleHead') }}
        <span class="text-main-primary inline-block transition-all delay-200"
              :class="isVisible ? 'scale-in' : 'scale-out'">
          {{ t('page4.titleHighlight') }}
        </span>
      </h2>

      <p class="text-[1.25rem] text-[#000000] transition-all delay-300"
         :class="isVisible ? 'fade-up-in' : 'fade-up-out'">
        {{ t('page4.desc') }}
      </p>
    </div>
    <!-- 图片展示区域 -->
    <div class="flex justify-center gap-[2.3rem]">
      <div 
        v-for="(image, index) in liveImages" 
        :key="image.id"
        class="relative cursor-pointer transition-all duration-700"
        :class="isVisible ? 'card-in' : 'card-out'"
        :style="{ transitionDelay: `${500 + index * 150}ms` }"
      >
        <!-- 图片容器 -->
        <div 
          class="w-[17.8rem] p-[1.2rem] flex items-center justify-center shadow-lg bg-[#fff] 
                 rounded-[1.5rem] h-[31rem] relative overflow-hidden group transform"
        >
          <!-- 图片 -->
          <img 
            :src="image.img" 
            class="w-full rounded-[1rem] object-contain relative z-10 transition-all duration-500"
          />
          <!-- 发光效果 -->
          <div 
            class="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 
                   opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 基础动画设置 */
.transform {
  will-change: transform, opacity;
  backface-visibility: hidden;
}

/* 标题滑入动画 */
.slide-down-in {
  opacity: 1;
  transform: translateY(0) translateZ(0);
}

.slide-down-out {
  opacity: 0;
  transform: translateY(-50px) translateZ(0);
}

/* 强调文字缩放动画 */
.scale-in {
  opacity: 1;
  transform: scale(1) translateZ(0);
}

.scale-out {
  opacity: 0;
  transform: scale(0.5) translateZ(0);
}

/* 描述文字淡入上升动画 */
.fade-up-in {
  opacity: 1;
  transform: translateY(0) translateZ(0);
}

.fade-up-out {
  opacity: 0;
  transform: translateY(30px) translateZ(0);
}

/* 卡片动画 */
.card-in {
  opacity: 1;
  transform: translateY(0) scale(1) translateZ(0);
}

.card-out {
  opacity: 0;
  transform: translateY(100px) scale(0.8) translateZ(0);
}

/* 阴影效果 */
.shadow-lg {
  box-shadow: 0 10px 30px rgba(0, 47, 142, 0.112);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 悬停效果增强 */
.group {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.group:hover {
  transform: translateY(-10px) scale(1.02);
}

.group:hover .shadow-lg {
  box-shadow: 0 20px 40px rgba(7, 112, 237, 0.2);
}

/* 图片悬停效果 */
.group:hover img {
  transform: scale(1.05);
}

/* 添加3D效果 */
.group {
  perspective: 1000px;
}

.group:hover {
  transform: translateY(-10px) scale(1.02) rotateY(5deg);
}
</style>